import { useClearMapEventsCache, useGetEventsForMapQuery } from "@ac-expo/api";
import { FilterEventType, HomePageEventType } from "@ac-expo/api-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface UseMapEventsOptions {
  filters: FilterEventType;
  enabled?: boolean;
}

interface UseMapEventsReturn {
  events: Array<
    Pick<
      HomePageEventType,
      "id" | "name" | "slug" | "address" | "isSponsored" | "dates" | "organiserName" | "image"
    >
  >;
  isLoading: boolean;
  isLoadingNewArea: boolean;
}

// TODO: Change in mobile as well: Custom hook for efficient map events management with client-side accumulation
export const useMapEvents = ({
  filters,
  enabled = true,
}: UseMapEventsOptions): UseMapEventsReturn => {
  const clearMapCache = useClearMapEventsCache();
  const prevFiltersRef = useRef<FilterEventType | null>(null);

  const [accumulatedEvents, setAccumulatedEvents] = useState<
    Array<
      Pick<
        HomePageEventType,
        "id" | "name" | "slug" | "address" | "isSponsored" | "dates" | "organiserName" | "image"
      >
    >
  >([]);

  const fetchedBoundsRef = useRef<Set<string>>(new Set());

  const [forceRefetch, setForceRefetch] = useState(false);

  const nonBoundsFilters = useMemo(() => {
    const { bounds, zoomLevel, ...rest } = filters;
    return rest;
  }, [filters]);

  useEffect(() => {
    if (prevFiltersRef.current) {
      const prevNonBounds = {
        ...prevFiltersRef.current,
        bounds: undefined,
        zoomLevel: undefined,
      };

      const currentNonBounds = {
        ...nonBoundsFilters,
        bounds: undefined,
        zoomLevel: undefined,
      };

      // Check if non-bounds filters have changed
      if (JSON.stringify(prevNonBounds) !== JSON.stringify(currentNonBounds)) {
        setAccumulatedEvents([]);
        fetchedBoundsRef.current.clear();
        setForceRefetch(true); // Force refetch after clearing
        clearMapCache(filters);
      }
    }

    prevFiltersRef.current = filters;
  }, [nonBoundsFilters, clearMapCache, filters]);

  const currentBoundsKey = useMemo(() => {
    return filters.bounds
      ? `${filters.bounds.northeast.lat.toFixed(4)},${filters.bounds.northeast.lng.toFixed(4)},${filters.bounds.southwest.lat.toFixed(4)},${filters.bounds.southwest.lng.toFixed(4)}`
      : null;
  }, [filters.bounds]);

  const shouldFetch = useMemo(() => {
    if (!currentBoundsKey || !enabled) return false;

    if (forceRefetch) return true;

    return !fetchedBoundsRef.current.has(currentBoundsKey);
  }, [currentBoundsKey, enabled, forceRefetch]);

  const {
    data: newEvents = [],
    isLoading,
    isFetching,
  } = useGetEventsForMapQuery(filters, {
    enabled: shouldFetch && !!filters.bounds,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const mergeEvents = useCallback(
    (
      existing: Array<
        Pick<
          HomePageEventType,
          "id" | "name" | "slug" | "address" | "isSponsored" | "dates" | "organiserName" | "image"
        >
      >,
      incoming: Array<
        Pick<
          HomePageEventType,
          "id" | "name" | "slug" | "address" | "isSponsored" | "dates" | "organiserName" | "image"
        >
      >,
    ) => {
      const eventMap = new Map();

      existing.forEach((event) => {
        eventMap.set(event.id, event);
      });

      incoming.forEach((event) => {
        eventMap.set(event.id, event);
      });

      return Array.from(eventMap.values());
    },
    [],
  );

  useEffect(() => {
    if (newEvents.length > 0 && currentBoundsKey) {
      setAccumulatedEvents((prev) => mergeEvents(prev, newEvents));

      fetchedBoundsRef.current.add(currentBoundsKey);

      setForceRefetch(false);
    }
  }, [newEvents, currentBoundsKey, mergeEvents]);

  const visibleEvents = useMemo(() => {
    return accumulatedEvents.filter((event) => {
      return event.address?.lat && event.address?.long;
    });
  }, [accumulatedEvents]);

  const isLoadingNewArea = isFetching && accumulatedEvents.length > 0;

  return {
    events: visibleEvents,
    isLoading: isLoading && accumulatedEvents.length === 0,
    isLoadingNewArea,
  };
};
